/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { RefObject } from "react"
import { useEffect, useRef } from "react"

import { animateCounter } from "./animate-counter"

/**
 * Formats a count compactly, such as "950", "1.2K" or "2.5M". English only,
 * like the rest of the console.
 */
const compactNumber = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 })

/**
 * Animates a span's displayed number to a target value without re-rendering
 * the component. The hook updates `textContent` directly, so the span must
 * have no React-managed children. Large numbers show compactly, such as "1.2K".
 *
 * Each animation starts from the last value displayed, allowing target changes
 * to interrupt an animation smoothly. On the first animation, values above 10
 * start at 90% of the target; smaller values start at zero. When reduced motion
 * is preferred, the target is displayed immediately. Nullish targets leave the
 * current display unchanged.
 *
 * The animation restarts when `value` or `duration` changes. Keep `duration`
 * stable across renders (for example, by initializing it with `useState`) to
 * avoid repeatedly restarting the animation.
 *
 * @param ref - The span whose text content is updated.
 * @param duration - Animation duration in milliseconds.
 * @param value - Target value, or `null`/`undefined` to leave the display unchanged.
 */
export function useAnimateValueEffect(
    ref: RefObject<HTMLSpanElement | null>,
    duration: number,
    value: number | undefined | null
): void {
    // Track the last displayed number so a new animation continues from the
    // current display rather than jumping back to its initial value.
    const shown = useRef<number | null>(null)

    useEffect(() => {
        if (value === null || value === undefined) return

        const display = (current: number) => {
            shown.current = current
            const text = compactNumber.format(current)
            // Skip the write while the text holds still, as it does for most
            // frames of a large count.
            if (ref.current && ref.current.textContent !== text) ref.current.textContent = text
        }

        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            display(value)
            return
        }

        const start = shown.current ?? (value > 10 ? value * 0.9 : 0)

        return animateCounter(start, value, duration, display)
    }, [duration, ref, value])
}
